import { defineStore } from 'pinia';
import { computed, Ref, ref } from 'vue';
import {
    AttributeDto,
    deletePolicyRoles,
    deletePolicySystems,
    getAllAccessPolicies,
    getAllSystemsAndRoles,
    getAllUserAttributes,
    getLibraryNames,
    getScopes,
    LibraryPolicy,
    LibraryPolicyDto,
    Policy,
    PolicyRole,
    PolicySystem,
    ScopeWithDescription,
    savePolicyRoles,
    savePolicySystems,
    saveUserAttribute,
    updatePolicySystems,
    deleteLibraryPolicy as deleteLibraryPolicyApi,
    saveLibraryPolicy as saveLibraryPolicyApi,
    expireUserAttribute as expireUserAttributeApi,
    restoreExpiredUserAttribute as restoreUserAttributesApi
} from '@/shared/helpers/api';
import { http } from '@/shared/httpWrapper';
import { checkIfExpired, UserAttributes } from '@/stores/accessPolicyStore.helper.ts';
import _ from 'lodash';

/** Scope the server stores when a user role is added without one. */
const DEFAULT_SCOPE = 'Default Scope';

export const useAccessPolicyStore = defineStore('accessPolicy', () => {
    const libraryPolicies: Ref<LibraryPolicy[]> = ref([]);
    const libraryNames: Ref<string[]> = ref([]);
    const policySystems: Ref<PolicySystem[]> = ref([]);
    const scopes: Ref<ScopeWithDescription[]> = ref([]);

    const userAttributes: Ref<UserAttributes[]> = ref([]);
    const newUserPolicies: Ref<Policy[]> = ref([]);
    const updateUserPolicies: Ref<Policy[]> = ref([]);

    const selectedLibrary: Ref<string | undefined> = ref(undefined);
    const selectedScope: Ref<string | undefined> = ref(undefined);
    const selectedSystem: Ref<PolicySystem | undefined> = ref(undefined);
    const selectedRole: Ref<PolicyRole | undefined> = ref(undefined);
    const selectedUser: Ref<string | undefined> = ref(undefined);

    const systemRoles = computed(() => {
        if (selectedSystem.value === undefined) {
            return [];
        }

        return selectedSystem.value?.roles;
    });

    async function loadLibraryPolicies(): Promise<void> {
        const res = await getAllAccessPolicies(http);
        libraryPolicies.value = res.flatMap(library =>
            library.policies.map(policy => ({
                libraryName: library.libraryName,
                id: library.id,
                policyId: policy.id,
                role: policy.facilityRole,
                system: policy.sourceSystem
            }))
        );
    }

    async function loadLibraryNames() {
        const res = await getLibraryNames(http);
        libraryNames.value = res.sort();
    }

    async function loadSystems(): Promise<void> {
        policySystems.value = await getAllSystemsAndRoles(http);
    }

    async function loadScopes(): Promise<void> {
        const res = await getScopes(http, 'Facility');
        scopes.value = res.sort((scope1, scope2) => scope1.identity.localeCompare(scope2.identity));
    }

    async function loadUserAttributes(): Promise<void> {
        const res = await getAllUserAttributes(http);
        userAttributes.value = res.flatMap(user =>
            user.policies.map(policy => ({
                email: user.email,
                id: user.id,
                name: user.fullName,
                fromCommonLib: policy.commonLibrary,
                policyId: policy.id,
                role: policy.facilityRole,
                scope: policy.scope,
                system: policy.sourceSystem,
                dateTimeExpire: policy.dateTimeExpire,
                isExpired: checkIfExpired(policy.dateTimeExpire, policy.commonLibrary)
            }))
        );
    }

    function clearSelection(): void {
        selectedLibrary.value = undefined;
        selectedSystem.value = undefined;
        selectedRole.value = undefined;
        selectedUser.value = undefined;
        selectedScope.value = undefined;
        newUserPolicies.value = [];
        updateUserPolicies.value = [];
    }

    function setSelectedSystem(policySystemName: string | null): void {
        const previousName = selectedSystem.value?.name;

        if (policySystemName === null) {
            selectedSystem.value = undefined;
        } else {
            const foundSystem: PolicySystem | undefined
                = policySystems.value.find(s => s.name === policySystemName);

            if (foundSystem) {
                selectedSystem.value = foundSystem;
            }
        }

        if (selectedSystem.value?.name !== previousName) {
            selectedRole.value = undefined;
        }
    }

    function setSelectedRole(policyRoleName: string | null): void {
        if (policyRoleName === null) {
            selectedRole.value = undefined;
            return;
        }

        const foundRole: PolicyRole | undefined
            = selectedSystem.value?.roles.find(s => s.name === policyRoleName);

        if (foundRole) {
            selectedRole.value = foundRole;
        }
    }

    function setSelectedScope(scope: string | null): void {
        selectedScope.value = scope ?? undefined;
    }

    function setSelectedLibrary(libraryName: string | null): void {
        selectedLibrary.value = libraryName ?? undefined;
    }

    function addUserPolicy(): boolean {
        if (selectedSystem.value === undefined || selectedRole.value === undefined) {
            return false;
        }

        const newPolicy: Policy = {
            id: 0,
            sourceSystem: selectedSystem.value.name,
            facilityRole: selectedRole.value.name,
            scope: selectedScope.value,
            commonLibrary: true,
            dateTimeExpire: ''
        };

        if (newPolicy.facilityRole === undefined || newPolicy.sourceSystem === undefined) {
            return false;
        }

        const alreadyPending = newUserPolicies.value.some(p =>
            p.sourceSystem === newPolicy.sourceSystem
            && p.facilityRole === newPolicy.facilityRole
            && p.scope === newPolicy.scope);
        if (alreadyPending) {
            return false;
        }

        // Replace the array rather than push: once b-table has received a new array (e.g. after a row is
        // removed) it keeps its own copy and only refreshes when the array reference changes.
        newUserPolicies.value = [...newUserPolicies.value, newPolicy];
        return true;
    }

    function isValidLibraryPolicy(): boolean {
        return !(selectedLibrary.value === undefined
            || selectedSystem.value === undefined
            || selectedRole.value === undefined);
    }

    function checkIfLibraryPolicyExists(): boolean {
        return libraryPolicies.value
            .some(p => p.libraryName === selectedLibrary.value
                && p.role === selectedRole.value?.name
                && p.system === selectedSystem.value?.name);
    }

    async function saveLibraryPolicy(): Promise<boolean> {
        if (selectedLibrary.value === undefined
            || selectedSystem.value === undefined
            || selectedRole.value === undefined
        ) {
            return false;
        }

        const policy: LibraryPolicyDto = {
            id: null,
            libraryName: selectedLibrary.value,
            sourceSystem: selectedSystem.value.name,
            facilityRole: selectedRole.value.name
        };
        await saveLibraryPolicyApi(http, selectedLibrary.value, policy);

        return true;
    }

    async function deleteLibraryPolicy(library: LibraryPolicy): Promise<void> {
        const libraryPolicy: LibraryPolicyDto = {
            libraryName: library.libraryName,
            id: library.policyId,
            facilityRole: library.role,
            sourceSystem: library.system
        };
        await deleteLibraryPolicyApi(http, libraryPolicy.libraryName, libraryPolicy);
    }

    /**
     * Pending roles the selected user does not have yet. Matches the server, which skips a role the user
     * already has (same system, role and scope), expired or not.
     */
    function pendingRolesNotYetAssigned(): Policy[] {
        const email = selectedUser.value?.toLowerCase();
        const existing = userAttributes.value.filter(a => a.email?.toLowerCase() === email);

        return newUserPolicies.value.filter(policy => !existing.some(a =>
            a.system === policy.sourceSystem
            && a.role === policy.facilityRole
            && a.scope === (policy.scope ?? DEFAULT_SCOPE)));
    }

    async function saveUserAttributes(): Promise<boolean> {
        if (selectedUser.value === undefined) {
            return false;
        }

        try {
            const email = selectedUser.value;
            const userAttributesToAdd: AttributeDto[] = pendingRolesNotYetAssigned()
                .map((policy: Policy) => {
                    return {
                        id: null,
                        email: email ?? null,
                        facilityRole: policy.facilityRole,
                        sourceSystem: policy.sourceSystem,
                        scope: policy.scope ?? DEFAULT_SCOPE,
                        commonLibrary: policy.commonLibrary ?? true
                    };
                });

            const userAttributesToUpdate: AttributeDto[] = updateUserPolicies.value
                .map((policy: Policy) => {
                    return {
                        id: policy.id,
                        email: email ?? null,
                        facilityRole: policy.facilityRole,
                        sourceSystem: policy.sourceSystem,
                        scope: policy.scope ?? DEFAULT_SCOPE,
                        commonLibrary: policy.commonLibrary ?? true,
                        extend: policy.extend
                    };
                });

            const apiSaveCalls: Promise<void>[] = userAttributesToAdd.map(async (dto) => {
                await saveUserAttribute(http, email, dto);
            });

            const apiUpdateCalls: Promise<void>[] = userAttributesToUpdate.map(async (dto) => {
                await expireUserAttributeApi(http, email, dto);
            });

            await Promise.all([...apiSaveCalls, ...apiUpdateCalls]);

            return true;
        } catch (_) {
            return false;
        }
    }

    const savePolicySystem = async (system: PolicySystem) => {
        await savePolicySystems(http, system);
    };

    const updatePolicySystem = async (system: PolicySystem) => {
        await updatePolicySystems(http, system);
    };

    const deletePolicySystem = async () => {
        if (selectedSystem.value !== undefined) {
            await deletePolicySystems(http, selectedSystem.value);
        }
    };

    const savePolicyRole = async (role: PolicyRole) => {
        await savePolicyRoles(http, role);
    };

    const deletePolicyRole = async (role: PolicyRole) => {
        await deletePolicyRoles(http, role);
    };

    async function expireUserAttributes(email: string, userPolicy: AttributeDto): Promise<boolean> {
        try {
            userPolicy.extend = false;
            await expireUserAttributeApi(http, email, userPolicy);
            return true;
        } catch (_) {
            return false;
        }
    }

    async function restoreUserAttributes(email: string, userPolicy: AttributeDto): Promise<boolean> {
        try {
            userPolicy.extend = true;
            await restoreUserAttributesApi(http, email, userPolicy);
            return true;
        } catch (_) {
            return false;
        }
    }

    function removeNewUserAttributes(policy: Policy): boolean {
        try {
            newUserPolicies.value = newUserPolicies.value
                .filter(p => !_.isEqual(p, policy));

            return true;
        } catch (_) {
            return false;
        }
    }

    return {
        selectedLibrary,
        selectedSystem,
        selectedRole,
        selectedUser,
        selectedScope,

        libraryPolicies,
        libraryNames,
        scopes,
        systemRoles,
        policySystems,
        userAttributes,
        newUserPolicies,
        updateUserPolicies,

        clearSelection,
        loadSystems,
        loadLibraryPolicies,
        loadLibraryNames,
        loadScopes,
        loadUserAttributes,

        setSelectedSystem,
        setSelectedRole,
        setSelectedScope,
        setSelectedLibrary,

        isValidLibraryPolicy,
        saveLibraryPolicy,
        deleteLibraryPolicy,
        checkIfLibraryPolicyExists,

        savePolicySystem,
        deletePolicySystem,
        updatePolicySystem,

        savePolicyRole,
        deletePolicyRole,

        addUserPolicy,
        pendingRolesNotYetAssigned,
        saveUserAttributes,
        expireUserAttributes,
        removeNewUserAttributes,
        restoreUserAttributes
    };
});
